import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const FastingCalendar = ({ onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthStats, setMonthStats] = useState({});
  const [loading, setLoading] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchMonthStats();
  }, [year, month]);

  const fetchMonthStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/commitments/month/${year}/${month + 1}`);
      const statsMap = {};
      response.data.forEach(stat => {
        statsMap[stat.date] = stat;
      });
      setMonthStats(statsMap);
    } catch (error) {
      console.error('Failed to fetch month stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDate = (day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  const isPast = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(year, month, day);
    return checkDate < today;
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  // Create calendar grid
  const calendarDays = [];
  
  // Empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const getCommitmentLevel = (count) => {
    if (!count) return 0;
    if (count >= 10) return 4;
    if (count >= 5) return 3;
    if (count >= 2) return 2;
    return 1;
  };

  return (
    <div className="w-full max-w-4xl mx-auto" data-testid="fasting-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <h2 className="font-display text-2xl sm:text-3xl text-foreground">
            {MONTHS[month]} {year}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToToday}
            className="text-sm text-muted-foreground hover:text-foreground"
            data-testid="calendar-today-btn"
          >
            Today
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={prevMonth}
            className="rounded-full"
            data-testid="calendar-prev-btn"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={nextMonth}
            className="rounded-full"
            data-testid="calendar-next-btn"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {DAYS.map(day => (
          <div 
            key={day} 
            className="text-center text-sm font-medium text-muted-foreground py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const dateStr = formatDate(day);
          const stat = monthStats[dateStr];
          const count = stat?.count || 0;
          const level = getCommitmentLevel(count);
          const past = isPast(day);
          const today = isToday(day);

          return (
            <button
              key={day}
              onClick={() => onDateSelect(dateStr, stat)}
              disabled={loading}
              data-testid={`calendar-day-${dateStr}`}
              className={`
                calendar-cell aspect-square rounded-xl p-2 flex flex-col items-center justify-center gap-1
                border transition-all duration-200
                ${today 
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                  : 'border-border/50 hover:border-primary/30'
                }
                ${past && !today ? 'opacity-60' : ''}
                ${level === 0 ? 'bg-white/50' : ''}
                ${level === 1 ? 'bg-sage-50' : ''}
                ${level === 2 ? 'bg-sage-100' : ''}
                ${level === 3 ? 'bg-sage-200' : ''}
                ${level === 4 ? 'bg-sage-300' : ''}
              `}
            >
              <span className={`
                text-sm sm:text-base font-medium
                ${today ? 'text-primary' : 'text-foreground'}
              `}>
                {day}
              </span>
              {count > 0 && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Users className="h-3 w-3" />
                  <span>{count}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-8 flex items-center justify-center gap-4 text-sm text-muted-foreground">
        <span>Fewer</span>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded bg-white/50 border border-border/50" />
          <div className="w-4 h-4 rounded bg-sage-50" />
          <div className="w-4 h-4 rounded bg-sage-100" />
          <div className="w-4 h-4 rounded bg-sage-200" />
          <div className="w-4 h-4 rounded bg-sage-300" />
        </div>
        <span>More fasters</span>
      </div>
    </div>
  );
};

export default FastingCalendar;
